import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const Downtown = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName} ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.downtown-calgary:About Paragraph">
          Downtown Calgary is the vibrant heart of the city, known for its
          bustling business district, stunning skyline, and cultural landmarks.
          Located along the Bow River, this area offers a dynamic mix of office
          towers, shopping centers, restaurants, and entertainment venues.
          Whether you’re visiting for work or leisure, Downtown Calgary offers
          the perfect blend of urban convenience and a high-energy atmosphere,
          with quick access to both natural beauty and city life.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$1,700 - $2,200',
          bed2: '$1,800 - $3,800',
          bed3: '$1,900 - $4,900'
        }}
        rentReportCity="Alberta"
        rentReportLink="https://liv.rent/blog/rent-reports/alberta/"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/%e9%98%bf%e5%b0%94%e4%bc%af%e5%a1%94/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.downtown-calgary:Apartment Hunting Paragraph 1">
            Compared to cities like Vancouver and Richmond, it’s fairly easy to
            find relatively cheap rentals in {{ neighborhoodNameWithCity }} -
            although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon. To start your search, you can refer to this
            guide on {{ neighborhoodNameWithCity }}
            neighbourhoods.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.downtown-calgary:Apartment Hunting Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long-term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short-term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.downtown-calgary:Apartments for rent Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Downtown Calgary
              </LinkText>{' '}
              are ideal for young professionals, students, and anyone looking to
              live in the city’s core. The area offers a range of modern
              apartment buildings with excellent amenities, including gyms,
              concierge services, and high-speed internet. Renting in Downtown
              Calgary allows residents to enjoy an urban lifestyle with easy
              access to offices, transit stations, shopping districts, and
              cultural attractions.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.downtown-calgary:Houses for rent Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Downtown Calgary
              </LinkText>{' '}
              are perfect for families or professionals who want the convenience
              of living near the city’s action without compromising on space.
              While Downtown is known for its high-rise buildings, there are
              also a variety of charming historic homes and luxury rentals.
              These properties provide a great opportunity to experience
              Calgary’s city center while enjoying private living with access to
              major amenities and attractions.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.downtown-calgary:Townhouses for rent Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Downtown Calgary
              </LinkText>{' '}
              offer the best of both worlds, combining the privacy and space of
              a house with the convenience of city living. These multi-level
              homes are ideal for families or roommates seeking proximity to
              work and entertainment. Many townhouses come with modern finishes,
              private garages, and outdoor spaces, making them a desirable
              option for those who want a quiet home without leaving the
              excitement of the downtown core.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.downtown-calgary:Basements for rent Paragraph">
              Basements for rent in Downtown Calgary are a great option for
              students, young professionals, or anyone seeking affordable
              housing in a prime location.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.downtown-calgary:Condos for rent Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Downtown Calgary
              </LinkText>{' '}
              are popular among professionals and individuals seeking a modern,
              low-maintenance lifestyle. These units typically offer sleek
              designs, high-end amenities, and breathtaking views of the city
              skyline. Condos are a great choice for those who enjoy urban
              living with easy access to dining, shopping, and cultural events,
              all within walking distance. With convenient transportation
              options, renting a condo in Downtown Calgary provides both luxury
              and accessibility.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.downtown-calgary:Rooms for rent Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Downtown Calgary
              </LinkText>{' '}
              are a great option for students, travelers, or professionals
              seeking an affordable way to live in the heart of the city. Shared
              accommodations are common in Downtown, offering a budget-friendly
              alternative to high-rise apartments. Renters can enjoy easy access
              to work, public transit, and Calgary’s popular downtown
              attractions while keeping rental costs low. Rooms in shared houses
              or apartments often come fully furnished and include utilities.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.downtown-calgary:Short term rentals Paragraph">
              <LinkText href={links.shortTerm}>
                Short-term rentals in Downtown Calgary
              </LinkText>{' '}
              are ideal for tourists, business professionals, or anyone visiting
              the city for a limited period. Whether you’re looking for a fully
              furnished apartment or a private room, Downtown offers a variety
              of rental options for short stays. These rentals are close to all
              the major attractions, dining options, and entertainment venues,
              providing visitors with a comfortable and convenient base from
              which to explore Calgary.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.downtown-calgary:Cheap rentals Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in {{ neighborhoodNameWithCity }}, there
              are neighbourhoods where rent is considerably cheaper, so
              exploring these options is a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.downtown-calgary:Exploring Paragraph">
          Downtown Calgary is the city’s vibrant core, offering a blend of
          modern architecture, cultural attractions, and natural beauty. Famous
          landmarks like the{' '}
          <LinkText href="https://www.calgarytower.com/">
            Calgary Tower
          </LinkText>{' '}
          and the{' '}
          <LinkText href="https://www.glenbow.org/">Glenbow Museum</LinkText>{' '}
          are located here, alongside the picturesque{' '}
          <LinkText href="https://www.ontariotrails.on.ca/trail/ottawa-river-pathway">
            Bow River Pathway
          </LinkText>
          . The area is also home to Calgary’s Financial District, entertainment
          venues, and shopping districts like{' '}
          <LinkText href="https://www.calgary.ca/planning/projects/stephen-avenue.html?redirect=/stephenavenue">
            Stephen Avenue
          </LinkText>
          . Whether you’re exploring its historic sites, attending events, or
          enjoying the great outdoors, Downtown Calgary offers an exciting urban
          experience.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.downtown-calgary:Things To Do Paragraph">
          Downtown Calgary, Alberta, is a vibrant district known for its blend
          of modern skyscrapers and historic landmarks. The area boasts
          attractions like the{' '}
          <LinkText href="https://www.calgarytower.com/">
            Calgary Tower
          </LinkText>
          , offering panoramic city views, and{' '}
          <LinkText href="https://www.calgary.ca/planning/projects/stephen-avenue.html?redirect=/stephenavenue">
            Stephen Avenue
          </LinkText>
          , a pedestrian mall lined with shops and eateries. Cultural
          enthusiasts can explore the{' '}
          <LinkText href="https://www.glenbow.org/">Glenbow Museum</LinkText>{' '}
          and Arts Commons, while nature lovers can relax in{' '}
          <LinkText href="https://www.calgary.ca/parks/princes-island-park.html">
            Prince’s Island Park
          </LinkText>{' '}
          along the Bow River. The neighborhood’s diverse offerings make it a
          hub for both residents and visitors seeking entertainment, culture,
          and urban living.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.downtown-calgary:Restaurants Paragraph">
        <Paragraph>
          Downtown Calgary offers a range of dining options for every taste.
          Here are three restaurants worth trying.
        </Paragraph>
        <Paragraph>
          1) <LinkText href="https://teatro.ca/">Teatro Restaurant</LinkText> -
          Teatro is a fine-dining Italian restaurant located in a heritage
          building in Downtown Calgary. Known for its sophisticated ambiance and
          exceptional service, Teatro offers a seasonal menu of fresh,
          locally-sourced ingredients. Signature dishes include homemade pasta
          and expertly prepared seafood. The elegant interior and award-winning
          wine list make it perfect for a special night out or a romantic
          dinner. make it ideal for family dinners and intimate gatherings.
        </Paragraph>
        <Paragraph>
          2){' '}
          <LinkText href="https://bvrrestaurant.com/">
            The Bow Valley Ranche Restaurant
          </LinkText>{' '}
          - Set in Fish Creek Provincial Park, The Bow Valley Ranche Restaurant
          offers a scenic and upscale dining experience just outside of Downtown
          Calgary. Known for its elegant setting and regional Alberta cuisine,
          the restaurant serves dishes crafted from locally-sourced ingredients.
          Guests can enjoy a wide range of meals from steaks to wild game, all
          while overlooking the picturesque park.
        </Paragraph>
        <Paragraph>
          3){' '}
          <LinkText href="https://www.thekeg.com/en/locations/calgary-4th-ave">
            The Keg Steakhouse + Bar
          </LinkText>{' '}
          - Located in the heart of Downtown Calgary, The Keg Steakhouse + Bar
          offers an upscale yet casual dining experience. Known for its tender
          steaks and signature cocktails, this steakhouse delivers consistently
          high-quality food. The spacious and stylish interior makes it a great
          spot for both business dinners and family gatherings.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.downtown-calgary:Transportation Paragraph">
          Getting around {{ neighborhoodNameWithCity }}, AB, is convenient with
          several transportation options. Calgary Transit operates a network of
          buses and the{' '}
          <LinkText href="https://www.calgarytransit.com/home.html">
            CTrain
          </LinkText>
          , a light rail system with two major lines—the Red Line and the Blue
          Line—connecting key areas of the city. For faster bus service, the{' '}
          <LinkText href="https://www.calgarytransit.com/rider-information/max.html">
            MAX Bus Rapid Transit
          </LinkText>{' '}
          (BRT) routes provide efficient connections. Calgary is also home to an
          extensive network of pathways and bikeways, making cycling a viable
          option. While some areas are walkable, the city is largely designed
          for driving, with major roads and highways like the Trans-Canada
          Highway and Deerfoot Trail providing easy access to different parts of
          Calgary. Whether by transit, bike, or car, {{ neighborhoodName }}{' '}
          offers accessible ways to explore the city.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.downtown-calgary:Where To Stay Paragraph">
          Accommodation options in {{ neighborhoodNameWithCity }} cater to those
          seeking a more local and immersive experience. This area features a
          mix of boutique guesthouses, bed-and-breakfasts, and{' '}
          <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
          available through platforms like{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>, offering
          travelers a unique and personalized stay. While large hotels might be
          limited within {{ neighborhoodName }} itself, its proximity to other
          parts of Calgary ensures that a wide range of traditional
          accommodations, from luxury to budget-friendly, are just a short
          distance away. Staying in {{ neighborhoodName }} gives visitors a
          chance to experience the area’s distinct character while remaining
          conveniently close to the city’s main attractions.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default Downtown
